import { WSFlexBox, WSPill, WSText } from "@wingspanhq/fe-component-library";
import {
  Currency,
  RecurringInvoice,
  RecurringLineItemType
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useMemo } from "react";
import { calculateRecurringLineItemsTotal } from "../utils";
import { Totals, TotalsSection, TotalsSectionItem } from "./Totals/Totals";

type Props = {
  recurringInvoice: RecurringInvoice;
};

export const InvoiceTemplateTotals: React.FC<Props> = props => {
  const invoiceTemplate = props.recurringInvoice.invoiceTemplate;
  const currency = props.recurringInvoice.currency as Currency;

  const totals = useMemo(() => {
    const lineItems = invoiceTemplate?.lineItems ?? [];
    const lineItemRows: TotalsSectionItem[] = [];

    lineItems.forEach((lineItem, index) => {
      const regularPrice = lineItem.unit
        ? (lineItem.unitCost ?? 0) * (lineItem.quantity ?? 0)
        : (lineItem.totalCost ?? 0);

      lineItemRows.push([
        <React.Fragment key={`line-item-${index}`}>
          <WSFlexBox.CenterY wrap="nowrap">
            <WSText color="gray600">{lineItem.description}</WSText>
            {lineItem.type === RecurringLineItemType.REIMBURSEMENT ? (
              <WSPill py="NONE" ml="M" text="Reimbursable" />
            ) : null}
          </WSFlexBox.CenterY>

          {lineItem.unit && (
            <WSText.ParagraphSm color="gray500">
              {lineItem.quantity} {lineItem.unit}(s) @{" "}
              {formatMoney(lineItem.unitCost ?? 0, currency)}/{lineItem.unit}
            </WSText.ParagraphSm>
          )}
          {lineItem.detail && (
            <WSText.ParagraphSm color="gray500">
              {lineItem.detail}
            </WSText.ParagraphSm>
          )}
        </React.Fragment>,
        formatMoney(regularPrice, currency)
      ]);

      const discount = lineItem.discount?.amount
        ? lineItem.discount.amount
        : lineItem.discount?.percentage
          ? (regularPrice * lineItem.discount.percentage) / 100
          : undefined;

      if (discount) {
        lineItemRows.push([
          <WSText key={`line-item-discount-${index}`} color="gray600">
            {lineItem.discount?.description}
            {lineItem.discount?.percentage
              ? ` (${lineItem.discount?.percentage}%)`
              : ""}
          </WSText>,
          formatMoney(-discount, currency)
        ]);
      }
    });

    const totalsSections: TotalsSection[] = [{ items: lineItemRows }];

    totalsSections.push({
      items: [
        {
          bold: true,
          left: "Total",
          right: formatMoney(
            calculateRecurringLineItemsTotal(lineItems),
            currency
          )
        }
      ]
    });

    return totalsSections;
  }, [currency, invoiceTemplate?.lineItems]);

  return <Totals sections={totals} />;
};
